/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CluUtteranceEntityLabel, CluUtteranceStore } from 'src/clu/core/stores/CluUtteranceStore';
import { LabelDto } from 'src/clu/modules/hooks/useCluUtteranceActions';
import { EntityStore } from 'src/core/types/EntityStore';
import { LabelingInteractionsEntityLabel } from 'src/luis/projects/utterances/handlers/labelingInteractionsUtils';
import { ModelType } from 'src/luis/types/ModelType';
import { LabelRange } from 'src/luis/types/stores/utterances/EntityLabelStore';
import { Label } from 'src/modules/labeling/types/Label';

export type DenormalizedEntityLabels = { entity: EntityStore } & LabelRange;

export const transformUtteranceLabels = (
    utterance: CluUtteranceStore,
    entities: EntityStore[]
): { labels: Label[]; conflicts: boolean[] } => {
    const conflicts = utterance?.tokenizedText.map(_ => false);
    const labels: Label[] = utterance?.labeledEntities.reduce((labelArray, label) => {
        const entityStore = entities.find(e => e.name === label.entityName);

        return !entityStore
            ? labelArray
            : labelArray.concat({
                  ...label,
                  renderedAs: 'line',
                  start: label.startTokenIndex,
                  end: label.endTokenIndex,
                  depth: 1,
                  renderingLevel: 1,
                  entity: { ...entityStore, typeId: ModelType.Simple, roles: [] },
                  intrinsicStart: label.startTokenIndex,
                  intrinsicEnd: label.endTokenIndex
              });
    }, []);

    return { labels, conflicts };
};

export const cluLabelsToLabelingInteractionsLabels = (label: CluUtteranceEntityLabel): LabelingInteractionsEntityLabel => ({
    entityId: label.entityName,
    start: label.startTokenIndex,
    end: label.endTokenIndex
});

export const labelingInteractionsLabelsToCluLabels = (label: LabelingInteractionsEntityLabel): CluUtteranceEntityLabel => ({
    entityName: label.entityId,
    startCharIndex: label.start,
    startTokenIndex: label.start,
    endCharIndex: label.end,
    endTokenIndex: label.end
});

export const cluUtteranceEntityLabelsToLabelDtoS = (labels: CluUtteranceEntityLabel[]): LabelDto[] =>
    labels.map(label => ({
        entityId: label.entityName,
        endTokenIndex: label.endTokenIndex,
        startTokenIndex: label.startCharIndex
    }));
